import clsx from 'clsx'
import { useRef } from 'react'

import mainStyles from 'assets/css/main.module.scss'
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg'
import { ReactComponent as DocumentIcon } from 'assets/icons/document.svg'
import BottomArc from 'components/bottomArc/BottomArc'
import GridCardLayout from 'components/cardGridLayout/CardGridLayout'
import { Button } from 'components/common/button/Button'
import { FrameworkWheelPage } from 'components/interactiveNavigation/FrameworkWheel'
import KeyTasks from 'components/keyTasks/KeyTasks'
import NavBar from 'components/navBar/NavBar'
import { ReactComponent as LinkIcon } from 'components/nmaModal/icons/link.svg'
import PageBanner from 'components/pageBanner/PageBanner'
import { ReturnToLandingButton } from 'components/returnToLandingButton/ReturnToLandingButton'
import { TopReturnToLandingButton } from 'components/topReturnToLandingButton/TopReturnToLandingButton'
import useHardNavigate from 'hooks/useHardNavigate'
import { AnalyticsActionType } from 'types/common/analytics.type'
import { EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './Learn.module.scss'

const Learn = () => {
  const keyTasksRef = useRef<HTMLDivElement>(null)
  const guidanceRef = useRef<HTMLDivElement>(null)
  const resourcesRef = useRef<HTMLDivElement>(null)

  const hardNavigate = useHardNavigate()

  return (
    <div className={styles.Learn}>
      <TopReturnToLandingButton />

      <PageBanner
        heading="Learn"
        description="While learning happens throughout the Loop, this phase focuses on gathering data, extracting insights, and codifying learnings for the next round of work."
        showBubble
        bubbleText="Study up on greatness"
        backgroundStyle={{ background: 'linear-gradient(180deg, #A7DEE2 0%, #6BC8CE 100%)' }}
        showNavigationCircle
        reducedTop
        bigTextFillColor="#6BC8CE"
        page={FrameworkWheelPage.Learn}
      />
      <NavBar
        items={[
          { key: 'key-tasks', title: 'Key Tasks', ref: keyTasksRef },
          { key: 'key-tasks', title: 'Guidance', ref: guidanceRef },
          { key: 'key-tasks', title: 'Resources', ref: resourcesRef },
        ]}
      />
      <KeyTasks
        ref={keyTasksRef}
        tasks={[
          {
            title: 'Drive in-flight optimization',
            description:
              'Drive in-flight optimization for media and creative with audience-level data at OU/ market level, and track learnings.',
            backgroundColor: '#E0F3F5',
          },
          {
            title: 'Collect key metrics',
            description: 'Partner with Human Insights, Media, and Studio X team to collect key data and learnings.',
            backgroundColor: '#E0F3F5',
          },
          {
            title: 'Organize a “Bar Raiser session” to extract key insights',
            description: 'Invite the core and extended team to share their perspective.',
            backgroundColor: '#E0F3F5',
          },
          {
            title: 'Codify learnings',
            description:
              'Document the key insights, such as why you decided to continue, iterate, or stop. Get your sponsor to review what you extracted as key insights and recommendations for the future!',
            backgroundColor: '#E0F3F5',
          },
        ]}
        inputs={[
          { text: 'Business performance' },
          { text: 'Brand performance' },
          { text: 'Media insights' },
          { text: 'Asset insights' },
          { text: 'Audience data (Studio X RT Dashboard, Learn X etc.)' },
        ]}
        outputs={[{ text: 'Documented learnings and insights' }, { text: 'Recommendations on how to proceed' }]}
        color="blue"
      />
      <div className="w-full bg-[#F4F4F4]">
        <GridCardLayout
          ref={guidanceRef}
          title="Guidance for Learn"
          titleDecoration="stars"
          decorationColor="blue"
          cards={[
            {
              title: 'The right data at the right time',
              description:
                'Define key data points based on objective. Leverage data sources (Beach, Simon, Excite, Learn X Audience Performance, etc.) Plan to complete process 2-3 months post-program.',
            },
            {
              title: 'Discussions over PPTs',
              description:
                'Don’t just share success stories. Ensure sufficient time for honest, transparent discussion to help elevate the next round of work.',
            },
            {
              title: 'Optimize for market reality',
              description:
                'For multi-market OUs, consider inviting lead markets and/or cluster markets based on similar needs and opportunities.',
            },
            {
              title: 'Involve network & sponsors',
              description:
                'Plan for feedback and discussion with the wider network and bring sponsors on board to sign off on final learnings and recommendations.',
            },
          ]}
        />

        <div className={clsx(mainStyles.container, styles.barRaiserSession)}>
          <div className={styles.title}>How to use Bar Raiser sessions</div>
          <div className={styles.stepsLayout}>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Prework / Performance Analysis</div>
              <div className={styles.cardDescription}>
                Core team consolidates metrics based on objectives (including learnings compiled during in-flight
                monitoring.)
              </div>
              <div className={styles.cardNumber}>1</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Core Team Bar Raiser Session</div>
              <div className={styles.cardDescription}>
                The core team runs an action-focused retro with Human Insights, Category, Media, and sponsor.{' '}
              </div>
              <div className={styles.cardNumber}>2</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Bar Raising with Markets & Capabilities</div>
              <div className={styles.cardDescription}>
                Core team collects input from markets and key capabilities, then runs a session to discuss what well and
                what could be better, resulting in three key learnings and action points.
              </div>
              <div className={styles.cardNumber}>3</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <div className={styles.cardTitle}>Documentation & Sign-off</div>
              </div>
              <div className={styles.cardDescription}>
                Core team creates a 'How to Raise the Bar on the Next Charter' document (including key data) and
                captures outcome in Workfront. Sponsor is debriefed and offers feedback and sign-off.
              </div>
              <div className={styles.cardNumber}>4</div>
            </div>
          </div>
        </div>

        <div className={clsx(mainStyles.container, styles.exploreYourResources)} ref={resourcesRef}>
          <div className={styles.title}>
            <span>Explore your resources</span>
          </div>

          <div className={styles.row}>
            <div />
            <div
              className={styles.item}
              onClick={() =>
                window.open(
                  'https://app.mural.co/t/europeoumuralartists4617/m/europeoumuralartists4617/1747921580238/3b68e34de00a273da91371004a3ebd1cd3efc810?sender=u2736ef7e3dacd2ee85bd6031',
                  'blank_',
                )
              }
            >
              <div className={styles.itemIcon}>
                <LinkIcon />
              </div>
              <div className={styles.itemText}>Bar Raiser Template</div>
            </div>

            <div
              className={styles.item}
              onClick={() =>
                window.open(
                  'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/c6e11e88-c782-44cb-b7ee-1fe3e99a2b60/public-download',
                  'blank_',
                )
              }
            >
              <div className={styles.itemIcon}>
                <DocumentIcon />
              </div>
              <div className={styles.itemText}>Bar Raiser Agenda</div>
            </div>
            <div />
          </div>
        </div>

        <div className={clsx(mainStyles.container)}>
          <div className="mt-[64px] flex justify-center">
            <Button
              variant="navigationControlBlack"
              text="Go to Produce"
              iconLeft={<ChevronRight className="rotate-180" />}
              border
              className="mr-[10px]"
              onClick={() => {
                hardNavigate(FrameworkWheelPage.Produce)
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: EVENTS.ACTION.LEARN.TO_PRODUCE,
                })
              }}
            />
            <ReturnToLandingButton variant="navigationControlHoverWhite" />
          </div>
        </div>
        <BottomArc backgroundColor="#F4F4F4" />
      </div>
    </div>
  )
}

export default Learn
